import React, {createContext, FC, useCallback, useContext, useEffect, useState} from 'react';
import {useLocalStorage} from '../useLocalStorage';
import {apiClient} from '../../services/apiClient';
import {store} from '../../store';
import {appInsights} from '../../services/appInsights';

interface AuthContextProps {
    localStorageTokenKeyName: string;
    user?: UserInfo;
    setUser: (info: UserInfo) => void;
    setToken: (token: string) => void;
    onLogin: (info: UserInfo) => void;
    onLogout: () => void;
    isInitialized: boolean;
}

interface AuthProviderProps {
    localStorageTokenKeyName?: string;
    authTokenKeyName?: string;
    initialCheckToken?: boolean;
    user?: UserInfo;
    onLogin?: (info: UserInfo) => void;
    onLogout?: () => void;
    children?: React.ReactNode;
}

export interface UserInfo {
    username?: string;
    token?: string;
    isLoggedIn: boolean;
    userInfo?: any;
}

const AuthContext = createContext({} as AuthContextProps);

const AuthProvider: FC<AuthProviderProps> = ({
    authTokenKeyName = 'token',
    localStorageTokenKeyName = 'token',
    user: _user = {isLoggedIn: false},
    onLogin: _onLogin,
    onLogout: _onLogout,
    initialCheckToken = true,
    children,
}) => {
    const [user, setUser] = useState<UserInfo>(_user || {isLoggedIn: false});
    const [isInitialized, setIsInitialized] = useState<boolean>(!initialCheckToken);
    const {getItem, removeItem, setItem} = useLocalStorage(localStorageTokenKeyName);

    const onLogin = useCallback(
        // TODO login response object?
        (info: any) => {
            const oldToken = getItem();
            const newToken = info[authTokenKeyName];

            if (!oldToken || !!newToken) {
                setItem(newToken);
            }

            // Handle both cases: info.userInfo exists OR userInfo is directly in info
            const userData = info?.userInfo || info;

            const newUser = {
                token: newToken || oldToken,
                userInfo: userData, // Store the actual user data
                username: info?.username || userData?.email,
                isLoggedIn: true,
            };

            setUser(newUser);

            // Set authenticated user context in Application Insights
            if (userData?.userId || userData?.id || userData?.email) {
                const userId = userData.userId || userData.id || userData.email;
                const accountId = userData.accountId || userData.customerId || userData.customer_id;
                appInsights.setAuthenticatedUserContext(
                    userId.toString().replace(/[\s,;=|]/g, '_'), // Remove invalid characters
                    accountId?.toString().replace(/[\s,;=|]/g, '_'), // Remove invalid characters
                    true // Store in cookie for session persistence
                );
            }

            if (_onLogin) _onLogin(info);
        },
        [_onLogin, authTokenKeyName, getItem, setItem]
    );

    const onLogout = useCallback(() => {
        setUser({
            isLoggedIn: false,
        });

        // Clear authenticated user context in Application Insights
        appInsights.clearAuthenticatedUserContext();

        // Clear Redux state
        try {
            store.dispatch({type: 'tests/resetTestsState'});
            store.dispatch({type: 'patient/resetPatientState'});
        } catch (error) {
            console.warn('Failed to reset Redux state on logout:', error);
        }

        // Clear all localStorage data
        try {
            localStorage.clear();
        } catch {
            // Fallback: try to remove specific items we know about
            try {
                localStorage.removeItem('token');
                localStorage.removeItem('currentPatient');
                localStorage.removeItem('userInfo');
            } catch {
                // Ignore fallback errors
            }
        }

        // Clear client-side cookies (Note: httpOnly cookies can only be cleared by server)
        try {
            const clearCookieForDomain = (cookieName: string, domain?: string) => {
                const domainStr = domain ? `;domain=${domain}` : '';
                const pastDate = new Date(0).toUTCString();

                // Try multiple path variations
                const paths = ['/', '/api', ''];
                paths.forEach((path) => {
                    document.cookie = `${cookieName}=;expires=${pastDate};path=${path}${domainStr}`;
                    if (domain) {
                        document.cookie = `${cookieName}=;expires=${pastDate};path=${path}`;
                    }
                });
            };

            // Get current cookies that JavaScript can access
            const cookies = document.cookie.split(';');
            const domain = window.location.hostname;
            const domainParts = domain.split('.');

            // Domains to try clearing cookies for
            const domainsToTry = [
                undefined, // Current domain without explicit domain attribute
                domain,
                '.' + domain,
            ];

            // If we have subdomains, also try parent domain
            if (domainParts.length > 2) {
                const parentDomain = '.' + domainParts.slice(-2).join('.');
                domainsToTry.push(parentDomain);
            }

            // Clear each cookie for each domain variation
            cookies.forEach((cookie) => {
                const cookieName = cookie.split('=')[0].trim();
                if (cookieName) {
                    domainsToTry.forEach((domainToTry) => {
                        clearCookieForDomain(cookieName, domainToTry);
                    });
                }
            });

            // Also try to clear common cookie names that might be httpOnly
            const commonCookieNames = ['refreshToken', 'refresh_token', 'refresh', 'session', 'auth', 'token'];
            commonCookieNames.forEach((cookieName) => {
                domainsToTry.forEach((domainToTry) => {
                    clearCookieForDomain(cookieName, domainToTry);
                });
            });
        } catch {
            // Ignore cookie clearing errors
        }

        // Clear session storage as well for completeness
        try {
            sessionStorage.clear();
        } catch {
            // Ignore cookie clearing errors
        }

        removeItem(); // This might be redundant after localStorage.clear() but keeping for safety
        if (_onLogout) _onLogout();
    }, [_onLogout, removeItem]);

    const setToken = useCallback(
        (token?: string) => {
            if (token === undefined) {
                setUser((old) => ({
                    ...old,
                    isLoggedIn: false,
                    token: undefined,
                }));
                removeItem();
            } else {
                setUser((old) => ({...old, token}));
                setItem(token);
            }
        },
        [removeItem, setItem]
    );

    useEffect(() => {
        if (initialCheckToken) {
            const oldToken = getItem();
            if (oldToken && !user?.isLoggedIn) {
                // Try to restore complete user data from localStorage
                try {
                    const storedUser = localStorage.getItem('user');
                    if (storedUser) {
                        const parsedUser = JSON.parse(storedUser);

                        // Restore the complete user state including userInfo
                        setUser({
                            token: oldToken,
                            userInfo: parsedUser.userInfo,
                            username: parsedUser.username,
                            isLoggedIn: true,
                        });

                        // Restore authenticated user context in Application Insights
                        if (parsedUser.userInfo) {
                            const userData = parsedUser.userInfo;
                            if (userData?.userId || userData?.id || userData?.email) {
                                const userId = userData.userId || userData.id || userData.email;
                                const accountId = userData.accountId || userData.customerId || userData.customer_id;
                                appInsights.setAuthenticatedUserContext(
                                    userId.toString().replace(/[\s,;=|]/g, '_'),
                                    accountId?.toString().replace(/[\s,;=|]/g, '_'),
                                    true
                                );
                            }
                        }
                    } else {
                        // Fallback: only restore token if no complete user data found
                        setUser({
                            token: oldToken,
                            isLoggedIn: true,
                        });
                    }
                } catch {
                    // Fallback: only restore token
                    setUser({
                        token: oldToken,
                        isLoggedIn: true,
                    });
                }

                // Sync token with API client
                apiClient.setToken(oldToken);
            }

            // Mark as initialized after checking token
            setIsInitialized(true);
        }
    }, [initialCheckToken, getItem, user?.isLoggedIn]);

    // Sync API client token whenever user token changes
    useEffect(() => {
        if (user?.token) {
            apiClient.setToken(user.token);
        } else {
            apiClient.clearToken();
        }
    }, [user?.token]);

    // Save user data to localStorage for impersonation recovery (simple sync)
    useEffect(() => {
        if (user?.isLoggedIn && user?.userInfo) {
            try {
                localStorage.setItem('user', JSON.stringify(user));
            } catch {
                // Ignore localStorage save errors
            }
        }
    }, [user]);

    // Set up callback for API client token updates (for refresh scenarios)
    useEffect(() => {
        const handleTokenUpdate = (newToken: string) => {
            if (newToken) {
                // Update localStorage with new token
                setItem(newToken);
                setUser((prevUser) => {
                    const updatedUser = {
                        ...prevUser,
                        token: newToken,
                        isLoggedIn: true,
                    };
                    return updatedUser;
                });
            } else {
                // Token was cleared
                removeItem();
                setUser({
                    isLoggedIn: false,
                });
            }
        };

        apiClient.setTokenUpdatedCallback(handleTokenUpdate);

        // Cleanup
        return () => {
            apiClient.setTokenUpdatedCallback(() => {});
        };
    }, [setItem, removeItem, user]);

    return (
        <AuthContext.Provider
            value={{
                localStorageTokenKeyName,
                user,
                setUser,
                setToken,
                onLogin,
                onLogout,
                isInitialized,
            }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuthContext must be used within an AuthContext.Provider');
    }
    return context;
};

export default AuthProvider;
